import { useId } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';

export interface ListPaginationProps {
  page: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  pageSizeOptions?: number[];
}

const DEFAULT_PAGE_SIZE_OPTIONS = [50, 100];

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const getPageItems = (currentPage: number, totalPages: number): Array<number | 'ellipsis'> => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, totalPages]);
  for (let page = currentPage - 1; page <= currentPage + 1; page += 1) {
    if (page > 1 && page < totalPages) {
      pages.add(page);
    }
  }

  const sortedPages = [...pages].sort((a, b) => a - b);
  const items: Array<number | 'ellipsis'> = [];

  sortedPages.forEach((page, index) => {
    const previous = sortedPages[index - 1];
    if (previous && page - previous > 1) {
      items.push('ellipsis');
    }
    items.push(page);
  });

  return items;
};

export const ListPagination = ({
  page,
  perPage,
  total,
  onPageChange,
  onPerPageChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: ListPaginationProps) => {
  const { t } = useTranslation();
  const perPageSelectId = useId();
  const safePerPage = Math.max(1, perPage);
  const safeTotal = Math.max(0, total);
  const totalPages = Math.max(1, Math.ceil(safeTotal / safePerPage));
  const currentPage = clamp(page, 1, totalPages);
  const from = safeTotal === 0 ? 0 : (currentPage - 1) * safePerPage + 1;
  const to = Math.min(currentPage * safePerPage, safeTotal);
  const pageItems = getPageItems(currentPage, totalPages);
  const selectablePageSizes = Array.from(new Set([...pageSizeOptions, safePerPage])).sort(
    (a, b) => a - b,
  );

  const changePage = (nextPage: number) => {
    const clampedPage = clamp(nextPage, 1, totalPages);
    if (clampedPage !== page) {
      onPageChange(clampedPage);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-efs-text-muted">
      <div className="min-w-0">
        {t('efs.common.pagination.range', {
          from,
          to,
          total: safeTotal,
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {onPerPageChange ? (
          <label className="flex items-center gap-2 whitespace-nowrap" htmlFor={perPageSelectId}>
            <span>{t('efs.common.pagination.per_page')}</span>
            <select
              id={perPageSelectId}
              className="h-9 rounded-md border border-input bg-transparent px-2 text-efs-text focus:outline-none focus:ring-1 focus:ring-ring"
              value={safePerPage}
              onChange={(event) => onPerPageChange(Number(event.target.value))}
            >
              {selectablePageSizes.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <Pagination
          aria-label={t('efs.common.pagination.label')}
          className="mx-0 w-auto justify-start"
        >
          <PaginationContent className="flex-wrap justify-start">
            <PaginationItem>
              <PaginationLink
                type="button"
                size="default"
                disabled={currentPage <= 1}
                aria-label={t('efs.common.pagination.previous')}
                className={cn('gap-1 pl-2.5', currentPage <= 1 && 'pointer-events-none opacity-50')}
                onClick={() => changePage(currentPage - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
                <span>{t('efs.common.pagination.previous')}</span>
              </PaginationLink>
            </PaginationItem>

            {pageItems.map((item, index) =>
              item === 'ellipsis' ? (
                <PaginationItem key={`ellipsis-${index}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={item}>
                  <PaginationLink
                    type="button"
                    isActive={item === currentPage}
                    aria-label={t('efs.common.pagination.page', { page: item })}
                    onClick={() => changePage(item)}
                  >
                    {item}
                  </PaginationLink>
                </PaginationItem>
              ),
            )}

            <PaginationItem>
              <PaginationLink
                type="button"
                size="default"
                disabled={currentPage >= totalPages}
                aria-label={t('efs.common.pagination.next')}
                className={cn(
                  'gap-1 pr-2.5',
                  currentPage >= totalPages && 'pointer-events-none opacity-50',
                )}
                onClick={() => changePage(currentPage + 1)}
              >
                <span>{t('efs.common.pagination.next')}</span>
                <ChevronRight className="h-4 w-4" />
              </PaginationLink>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
};
